import { addDoc, collection, deleteDoc, doc, getDoc, getDocs, query, updateDoc, where } from "firebase/firestore";
import { HiredTeacherPlacements } from "models";
import { db } from "services";
import { dbCollections } from "utilities";

/**
 * Retrieves a list of all hired placements from firebase, using the map view type.
 * @returns A Promise containing the list of all hired placement teachers.
 */

export async function getAllHiredPlacements(): Promise<HiredTeacherPlacements[]> {
  const hiredQuery = query(
    collection(db, dbCollections.memberInfo),
    where("type", "==", "hired")
  );
  const snipshot = await getDocs(hiredQuery);
  return snipshot.docs.map(doc => {
    return { ...doc.data(), id: doc.id } as HiredTeacherPlacements;
  });
}

/**
 * Retrieves more detailed data for one specific hired placement on PeopleGrove.
 * @param id The username of a specific PeopleGrove hired placement.
 * @returns A more detailed blob of data for a specific hired placement on PeopleGrove.
 */
export async function getHiredPlacement(id: string): Promise<HiredTeacherPlacements | undefined> {
  const docRef = doc(db, dbCollections.memberInfo, id);
  const snapshot = await getDoc(docRef);
  return snapshot.exists()
    ? ({ ...snapshot.data(), id: snapshot.id } as HiredTeacherPlacements)
    : undefined;
}

/**
 * Retrieves a specific hired placement from the database as a HiredPlacement.
 * @param id The ID of the hired placement to retrieve.
 * @returns A Promise containing a specific hired placement, or undefined if the ID doesn't exist.
 */
export async function getHiredPlacementStory(
  id: string,
): Promise<HiredTeacherPlacements | undefined> {
  const docRef = doc(db, dbCollections.memberInfo, id);
  const snapshot = await getDoc(docRef);
  return snapshot.exists()
    ? ({ ...snapshot.data(), id: snapshot.id } as HiredTeacherPlacements)
    : undefined;
}

/**
 * Updates an existing hired placement in the database. Some or all fields may
 * be updated, depending on what's included in the newHiredPlacement param.
 * @param id The ID of the hired placement to update.
 * @param newHiredPlacement The updated version of the hired placement.
 * @returns A Promise representing the status of the update.
 */
export async function updateHiredPlacementStory(
  id: string,
  newHiredPlacement: HiredTeacherPlacements,
): Promise<void> {
  const docRef = doc(db, dbCollections.memberInfo, id);
  return updateDoc(docRef, { ...newHiredPlacement });
}

/**
 * Adds a new hired placement to the database and returns the id of the created document.
 * @param newHiredPlacement The hired placement to add.
 * @returns A Promise containing the ID of the added hired placement.
 */
export async function addHiredPlacementStory(
  newHiredPlacement: HiredTeacherPlacements,
): Promise<string> {
  const collectionRef = collection(db, dbCollections.memberInfo);
  const snapshot = await addDoc(collectionRef, newHiredPlacement);
  return snapshot.id;
}

/**
 * Deletes an item from the database.
 * @param id The ID of the item to delete.
 * @returns A Promise representing the status of the delete.
 */
export async function deleteHiredStory(id: string): Promise<void> {
  const docRef = doc(db, dbCollections.memberInfo, id);
  return deleteDoc(docRef);
}
